/**
 * Reports home (`/reports`; design §1): pinned saved views, then the report families as cards, each with up to three
 * live headline tiles and the freshness line. The catalogue already lists only the reports my role may open
 * (registry.visible_to); profitability links to /profit, licences says "Not connected yet" until the Pax8 slice ships.
 */
import { buttonVariants, Chip, cn, EmptyState } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowRight, CircleAlert, Clock, Pin, PlugZap, Settings2 } from 'lucide-react'
import type * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import type { Drill, ReportCatalogue, ReportDef, SavedViewOut } from '../../lib/api/generated'
import { useMe } from '../auth/session'
import { catalogueQuery, drillSearch, isNativeReport, type ReportsSearch, searchFromFilters, viewsQuery } from './api'
import { KpiTile } from './charts'
import { useCanSee } from './roles'
import { ScheduleBadge } from './schedule'
import { freshnessLine, QueryView } from './shared'

export const FAMILIES: { key: ReportDef['family']; title: string }[] = [
  { key: 'desk', title: 'Desk' },
  { key: 'people', title: 'People and customers' },
  { key: 'field', title: 'Field' },
  { key: 'money', title: 'Money' },
  { key: 'projects', title: 'Projects' },
  { key: 'riley', title: 'Riley' },
]

function ReportLink({
  def,
  brand,
  children,
  className,
}: {
  def: ReportDef
  brand?: string
  children: React.ReactNode
  className?: string
}) {
  if (def.key === 'profitability') {
    return (
      <Link to="/profit" className={className}>
        {children}
      </Link>
    )
  }
  return (
    <Link to="/reports/$reportId" params={{ reportId: def.key }} search={brand ? { brand } : {}} className={className}>
      {children}
    </Link>
  )
}

function StateLine({ def }: { def: ReportDef }) {
  if (def.state === 'not_connected') {
    return (
      <Chip tone="neutral" size="lg">
        <PlugZap aria-hidden /> Not connected yet
      </Chip>
    )
  }
  if (def.state === 'unavailable') {
    return (
      <Chip tone="danger" size="lg">
        <CircleAlert aria-hidden /> Couldn't load
      </Chip>
    )
  }
  if (def.state === 'stale') {
    return (
      <Chip tone="warning" size="lg">
        <Clock aria-hidden /> Out of date
      </Chip>
    )
  }
  return null
}

function ReportCardView({ def, brand, onDrill }: { def: ReportDef; brand?: string; onDrill: (d: Drill) => void }) {
  const canSee = useCanSee()
  const headline = (def.headline ?? []).filter((m) => canSee(m.visibility)).slice(0, 3)
  return (
    <article
      aria-labelledby={`report-${def.key}`}
      data-report={def.key}
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-xs"
    >
      <header className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 id={`report-${def.key}`} className="text-base font-semibold">
            <ReportLink def={def} brand={brand} className="hover:underline">
              {def.title}
            </ReportLink>
          </h3>
          <p className="text-sm text-muted-foreground">{def.summary}</p>
        </div>
        <StateLine def={def} />
      </header>
      {def.state === 'not_connected' ? null : headline.length ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {headline.map((m) => (
            <KpiTile key={m.key} metric={m} onDrill={onDrill} canSee={canSee} />
          ))}
        </div>
      ) : null}
      <ReportLink
        def={def}
        brand={brand}
        className="mt-auto inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
      >
        Open {def.kind === 'native' ? 'report' : def.title.toLowerCase()} <ArrowRight className="size-4" aria-hidden />
      </ReportLink>
    </article>
  )
}

function PinnedViews({ views, brand }: { views: SavedViewOut[]; brand?: string }) {
  if (!views.length) return null
  return (
    <section aria-labelledby="pinned-views" className="space-y-2">
      <h2 id="pinned-views" className="text-sm font-semibold text-muted-foreground">
        Pinned views
      </h2>
      <ul className="flex flex-wrap gap-2">
        {views.map((v) =>
          isNativeReport(v.report) ? (
            <li key={v.id}>
              <Link
                to="/reports/$reportId"
                params={{ reportId: v.report }}
                search={{ ...searchFromFilters(v.filters), brand: v.filters.brand ?? brand }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium hover:border-primary hover:text-primary"
              >
                <Pin className="size-3.5 text-muted-foreground" aria-hidden />
                {v.name}
                <ScheduleBadge schedule={v.schedule} />
              </Link>
            </li>
          ) : null,
        )}
      </ul>
    </section>
  )
}

export function HomeBody({ catalogue, pinned, brand }: { catalogue: ReportCatalogue; pinned: SavedViewOut[]; brand?: string }) {
  const navigate = useNavigate()
  const fresh = freshnessLine(catalogue.fact_run)
  // Home tiles are last-7-days figures, so the drill opens on that same window.
  const onDrill = (d: Drill) =>
    void navigate({
      to: '/reports/$reportId',
      params: { reportId: d.report },
      search: { ...drillSearch(d, d.report), range: '7d', brand },
    })
  const families = FAMILIES.map((f) => ({ ...f, reports: catalogue.reports.filter((r) => r.family === f.key) })).filter(
    (f) => f.reports.length,
  )
  return (
    <div className="space-y-6">
      <p
        role="status"
        data-tone={fresh.tone}
        className={cn(
          'text-sm',
          fresh.tone === 'ok' ? 'text-muted-foreground' : fresh.tone === 'warn' ? 'text-warning' : 'text-destructive',
        )}
      >
        {fresh.text}
      </p>
      <PinnedViews views={pinned} brand={brand} />
      {families.length === 0 ? <EmptyState title="No reports are available to your role yet." /> : null}
      {families.map((f) => (
        <section key={f.key} aria-labelledby={`family-${f.key}`} data-family={f.key} className="space-y-3">
          <h2 id={`family-${f.key}`} className="text-lg font-semibold">
            {f.title}
          </h2>
          <div className="grid grid-cols-1 gap-4 pane:grid-cols-2 2xl:grid-cols-3">
            {f.reports.map((def) => (
              <ReportCardView key={def.key} def={def} brand={brand} onDrill={onDrill} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

export function ReportsHome() {
  const me = useMe()
  const search = useSearch({ strict: false }) as ReportsSearch
  const catalogue = useQuery(catalogueQuery(search.brand))
  const views = useQuery(viewsQuery(search.brand))
  // Pinned is "on my Reports home": my own pinned views. A failed views read hides the row (the reports still render).
  const pinned = (views.data ?? []).filter((v) => v.pinned && v.owner.id === me?.id)
  return (
    <Frame
      active="reports"
      railless
      main={
        <div className="reports-root flex h-full flex-col overflow-y-auto" data-scroll-top>
          <PaneHeader
            title="Reports"
            subtitle="How the desk, the team, the field and the money are doing."
            actions={
              catalogue.data?.can_edit_settings ? (
                <Link
                  to="/reports/settings"
                  search={search.brand ? { brand: search.brand } : {}}
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  <Settings2 className="size-4" aria-hidden />
                  Settings
                </Link>
              ) : null
            }
          />
          <main className="w-full max-w-7xl px-4 py-4 sm:px-5">
            <QueryView query={catalogue} what="The reports catalogue">
              {(data) => <HomeBody catalogue={data} pinned={pinned} brand={search.brand} />}
            </QueryView>
          </main>
        </div>
      }
    />
  )
}
